import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  Lock,
  MessageSquare,
  PlayCircle,
  RefreshCw,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';

/**
 * Painel de acompanhamento do Módulo Inbox Omnichannel.
 *
 * Não é a caixa de entrada (isso é a Fase 3, rota /inbox). Esta tela existe
 * para responder "o que já ficou pronto e o que falta", enquanto o módulo está
 * em construção. Duas camadas:
 *
 *  1. Roadmap ESTÁTICO das 16 fases. A fonte de verdade é a Central de
 *     Solicitações (1 solicitação mãe + 15 subtarefas); aqui é a leitura
 *     rápida, sem precisar abrir tarefa por tarefa.
 *  2. Estado AO VIVO das conexões, lido de `inbox_connections_public`. É a
 *     única parte que pode divergir do roadmap — e é de propósito: mostra o
 *     que o banco realmente tem, não o que o plano diz que deveria ter.
 *
 * Quando a Fase 3 entregar a caixa de entrada, esta tela continua útil como
 * painel de operação das fases restantes.
 */

type StatusFase = 'concluida' | 'em_andamento' | 'bloqueada' | 'nao_iniciada';
type Responsavel = 'Victor' | 'Vinicius';

interface ItemChecklist {
  texto: string;
  feito: boolean;
  /** Detalhe curto: por que está pendente, ou o que comprova que ficou pronto. */
  nota?: string;
}

interface Fase {
  numero: number;
  titulo: string;
  responsavel: Responsavel;
  status: StatusFase;
  resumo: string;
  /** Fases anteriores que precisam estar verdes antes desta começar. */
  dependeDe?: number[];
  checklist?: ItemChecklist[];
  /** Aviso que não cabe no checklist e não pode ser perdido. */
  alerta?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
/**
 * O que destrava cada item que falta.
 *
 * A ordem é a do prazo: o que demora semanas e não depende de ninguém aqui vem
 * primeiro, e o que é uma tarde de trabalho vem por último. Assim quem abre a
 * tela vê primeiro o que precisa ser começado hoje para não atrasar o resto.
 */
const GRUPOS = {
  meta: {
    titulo: 'Espera a Meta',
    explicacao: 'Verificação, App Review e credenciais. Leva semanas e não depende de nós.',
    classe: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
  chip: {
    titulo: 'Espera o chip',
    explicacao: 'Precisa de uma linha NOVA: número com histórico dispara evento de toda conversa antiga.',
    classe: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  decisao: {
    titulo: 'Espera uma decisão',
    explicacao: 'Não é técnico — é escolher como deve funcionar.',
    classe: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  },
  fazer: {
    titulo: 'Falta fazer',
    explicacao: 'Trabalho nosso que ainda não foi escrito. Não espera ninguém.',
    classe: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  },
  uso: {
    titulo: 'Espera alguém usar',
    explicacao: 'Está pronto e nunca foi exercitado por quem atende.',
    classe: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  },
  limpeza: {
    titulo: 'Limpeza no go-live',
    explicacao: 'Dado de exemplo que sai quando a tela não precisar mais dele.',
    classe: 'bg-gray-200 text-gray-700 dark:bg-slate-700 dark:text-slate-300',
  },
} as const;

// Roadmap
//
// Espelha as 15 subtarefas da solicitação "Módulo Inbox Omnichannel" mais a
// Fase 0. Ao concluir uma fase, atualize `status` e o checklist AQUI e na
// Central de Solicitações — as duas precisam contar a mesma história.
// ─────────────────────────────────────────────────────────────────────────────
const FASES: Fase[] = [
  {
    numero: 0,
    titulo: 'Fundação: schema, RLS, filas e contrato de adapters',
    responsavel: 'Victor',
    status: 'concluida',
    resumo:
      'Todo o schema do módulo, RLS em 15 tabelas, filas pgmq, bucket privado e o contrato de adapters. Nada aqui alterou tabela existente do VigorHub.',
    checklist: [
      { texto: 'Enums, tabelas e índices (prefixo inbox_)', feito: true },
      { texto: 'RLS ligada em todas as tabelas do módulo', feito: true },
      {
        texto: 'Correção: RLS nas partições de inbox_webhook_events',
        feito: true,
        nota: 'Partições nasceram antes do ALTER e ficaram legíveis pelo PostgREST. Tabelas vazias, nada vazou.',
      },
      {
        texto: 'Funções de RLS movidas para o schema inbox_private',
        feito: true,
        nota: 'Saíram do PostgREST sem perder o EXECUTE que as policies precisam.',
      },
      { texto: 'Filas pgmq: inbound, outbound, media', feito: true },
      { texto: 'Bucket privado inbox-media', feito: true },
      { texto: 'Contrato de adapters + matriz de capabilities', feito: true },
    ],
  },
  {
    numero: 1,
    titulo: 'Infra Evolution API na VPS',
    responsavel: 'Victor',
    status: 'concluida',
    resumo:
      'Único componente do módulo que não é SaaS. Sobe na VPS da Hostinger, ao lado do n8n de produção, atrás do Traefik que já está lá.',
    alerta:
      'O pareamento inicial usa um número pessoal, temporário. Enquanto ele estiver pareado, WEBHOOK_GLOBAL_ENABLED fica false — com a ingestão ligada, conversa pessoal de terceiros passaria a ser gravada no banco da Vigor, e a purga da Fase 15 ainda não existe.',
    checklist: [
      { texto: 'Stack Docker: Evolution + Postgres + Redis, com volume de sessão', feito: true },
      { texto: 'Tag de imagem fixa (nada de latest)', feito: true },
      { texto: 'Configuração do Traefik levantada na VPS', feito: true, nota: 'root_default / websecure / mytlschallenge' },
      { texto: 'Labels do Traefik + headers de segurança no compose', feito: true },
      { texto: 'Snapshot da VPS', feito: true, nota: '07/09/2026 10:57' },
      { texto: 'Acesso SSH configurado', feito: true },
      { texto: 'DNS de evo.vigorenergy.com.br propagando', feito: true, nota: 'A → 148.230.77.100, autoritativo Hostinger' },
      { texto: 'Scripts: preflight, backup e smoke test', feito: true },
      { texto: 'Gerar os 3 segredos e preencher o .env na VPS', feito: true },
      { texto: 'docker compose up e certificado emitido', feito: true, nota: 'Evolution 2.3.7, os 3 containers healthy, TLS verificado de fora.' },
      { texto: 'Instância teste-fase1 criada e QR pareado', feito: true },
      { texto: 'Smoke test verde nos 4 critérios de aceite', feito: true, nota: 'Restart manteve a sessão — o volume de sessão funciona.' },
      { texto: 'Segredos gravados no Supabase Vault', feito: true, nota: 'API key e token de webhook. São do servidor, sobrevivem à troca de chip.' },
      { texto: 'Encerrar o pareamento temporário dos dois lados', feito: true, nota: 'Feito em 07/09: logout ANTES do delete, porque apagar direto deixaria a sessão viva no celular e o aparelho continuaria listado lá. fetchInstances devolve zero, reconferido em 08/09.' },
      { texto: 'Chip definitivo → instância vigor-comercial', feito: false, nota: 'Chip ainda não comprado. É o que libera a Fase 2.' },
    ],
  },
  {
    numero: 2,
    titulo: 'Ingestão do WhatsApp não-oficial',
    responsavel: 'Victor',
    status: 'concluida',
    resumo:
      'Primeiro canal ponta a ponta. Webhook grava o evento cru, enfileira e responde 200 — nunca processa no request.',
    alerta:
      'Código pronto, deployado e validado com payload sintético — nenhuma mensagem real foi ingerida. Falta a janela ao vivo para confirmar o formato real do payload da Evolution, e o chip definitivo antes de ligar WEBHOOK_GLOBAL_ENABLED.',
    checklist: [
      { texto: 'Adapter whatsapp_evolution, registrado no index.ts', feito: true },
      { texto: 'Edge function inbox-webhook-evolution', feito: true, nota: 'Deployada. Sem token 401, token errado 401, token certo 200.' },
      { texto: 'Worker inbound', feito: true, nota: 'Deployado e validado: 3 eventos → 2 mensagens, 1 contato, 1 conversa, duplicata ignorada.' },
      { texto: 'Worker de mídia em fila separada', feito: true, nota: 'Imagem gravada no bucket e em inbox_attachments.' },
      { texto: 'Heartbeat: last_seen_at e erro após 5 min', feito: true, nota: 'Cron a cada 2 min. Marcou a conexão de teste sozinho.' },
      { texto: 'Broadcast em inbox:org:{org_id}', feito: true, nota: 'É daqui que a Fase 3 consome. Validação real só com tela escutando.' },
      { texto: 'Testes do adapter', feito: true, nota: '65 testes passando.' },
      { texto: 'Validar contra payload REAL da Evolution', feito: true, nota: 'Janela ao vivo 07/09: 71 eventos, assinatura válida em todos, 9 mensagens gravadas. Tudo apagado depois.' },
      { texto: 'Mídia real via downloadMedia', feito: true, nota: 'Foto real baixada e gravada. Corrigida a extensão, que saía .bin.' },
      { texto: 'Reagendar os workers no go-live', feito: true, nota: 'Conferido no banco em 07/09: inbound, outbound e events de minuto em minuto, heartbeat de 2 em 2, todos com resposta 200 real. FALTA o worker de MÍDIA — sem ele, foto e áudio entram sem anexo.' },
      { texto: 'Ligar WEBHOOK_GLOBAL_ENABLED', feito: false, nota: 'SÓ com o chip definitivo. É o último passo, não o primeiro.' },
    ],
  },
  {
    numero: 3,
    titulo: 'Inbox somente leitura',
    responsavel: 'Vinicius',
    status: 'em_andamento',
    resumo: 'A tela: lista de conversas, thread e painel de contexto. Realtime por broadcast, ordenação por provider_timestamp.',
    alerta:
      'A tela está de pé em /inbox, construída e conferida com um lote de dados sintéticos (5 conversas, 546 mensagens) inserido direto no banco. Os três critérios de aceite só fecham com WhatsApp conectado de verdade — o que depende do chip, igual às Fases 1 e 2.',
    checklist: [
      { texto: 'Rota /inbox em três colunas, no design system atual', feito: true, nota: 'Nenhuma biblioteca de componentes nova. O menu Omnichannel virou Caixa de entrada + Acompanhamento.' },
      { texto: 'Lista virtualizada com react-window', feito: true, nota: 'v2 (rowComponent/rowProps). Já estava instalado — o card dizia que não.' },
      { texto: 'View inbox_conversas_lista: contato, conexão e prévia em uma consulta', feito: true, nota: 'Sem ela seriam 1 + N consultas por render. security_invoker, então a RLS continua mandando.' },
      { texto: 'Thread com paginação de 50, agrupamento por dia e anexos assinados', feito: true, nota: 'Bucket é privado: sem URL assinada a imagem falha em silêncio.' },
      { texto: 'Ordenação por provider_timestamp em toda a tela', feito: true },
      { texto: 'Realtime por broadcast em inbox:org:{org_id}', feito: true, nota: 'Assina os dois eventos. O payload de mensagem.recebida é parcial, então a lista sempre relê do banco.' },
      { texto: 'Banner de conexão, com o aviso permanente do canal não-oficial', feito: true, nota: 'Lido de capabilities.isUnofficial, sem if por canal. Separa "o WhatsApp caiu" de "esta aba caiu".' },
      { texto: 'Painel de contexto com o vínculo de UC e negociação', feito: true },
      { texto: 'Composer + envio, retry de mensagem falha (Fase 5 na UI)', feito: true, nota: 'Estava sem dono no card da Fase 5. Dirigido por capabilities; erro real vem de error.context.' },
      { texto: 'Acesso por inbox_members, não por profile.role', feito: true, nota: 'Fechado nos 3 do projeto em 07/09. Antes eram 5 pessoas, no menu e na RLS.' },
      { texto: 'Testes dos formatadores', feito: true, nota: '11 testes em node:test: telefone, prévia de mídia, virada de dia e fuso.' },
      { texto: 'Mensagem real aparece em menos de 5s sem refresh', feito: false, nota: 'Critério de aceite. Precisa da janela ao vivo com o chip — o broadcast nunca foi exercitado com tela escutando.' },
      { texto: '500 mensagens rolam sem travar', feito: true, nota: 'Thread pagina de 50 em 50 e os balões são memoizados: recarga da lista, timer de conexão ou tecla no composer não repintam as 520. Conversa de teste do Portal dos Buritis.' },
      { texto: 'O banner reflete o estado real da conexão', feito: false, nota: 'Só dá para provar derrubando uma conexão de verdade.' },
      { texto: 'Apagar o lote sintético do banco', feito: false, nota: 'Sai antes do go-live: 1 conexão, 5 contatos, 5 conversas e 546 mensagens marcadas com metadata.seed.' },
    ],
  },
  {
    numero: 4,
    titulo: 'Gestão de conexões',
    responsavel: 'Vinicius',
    status: 'em_andamento',
    dependeDe: [3],
    resumo: 'Administração dos canais: pareamento por QR, reconectar, status e último erro. Lê a view pública, nunca a tabela com o segredo.',
    alerta:
      'É esta tela que vai ser usada no dia do chip, e por isso o botão que LIGA a ingestão é separado do que cria a instância: criar e parear não grava conversa nenhuma. Ligar é o último passo, com confirmação escrita na cara.',
    checklist: [
      { texto: 'Edge function inbox-conexoes (criar, QR, estado, reiniciar, desconectar, webhook)', feito: true, nota: 'Deployada. A chave da Evolution sai do Vault e nunca chega ao browser.' },
      { texto: 'Endpoints conferidos contra a Evolution 2.3.7 de produção', feito: true, nota: 'Instância descartável, sem parear ninguém, apagada no fim. Achou um erro: logout devolve 400 em instância já caída — agora tratado como sucesso.' },
      { texto: 'verify_jwt = true + só admin do Inbox', feito: true, nota: 'Conferido: sem JWT 401, com anon key 401. O papel vem de inbox_members, não de profile.role.' },
      { texto: 'Tela /inbox/configuracoes, restrita a admin', feito: true },
      { texto: 'Lista com canal, nome, status, último sinal e último erro', feito: true, nota: 'Lida da view pública inbox_connections_public.' },
      { texto: 'Criar instância e parear por QR, com o QR renovando sozinho', feito: true, nota: 'QR novo a cada 20s, estado a cada 3s. A tela vira para conectado sem reload.' },
      { texto: 'Reiniciar e desconectar', feito: true, nota: 'Desconectar pede confirmação: encerra a sessão do celular.' },
      { texto: 'Interruptor da ingestão, separado da criação', feito: true, nota: 'A instância nasce com o webhook DESLIGADO. É a trava que impede gravar conversa de terceiros antes do chip.' },
      { texto: 'Espaço preparado para e-mail (7) e Meta (12/13)', feito: true },
      { texto: 'QR escaneado leva a active sem reload manual', feito: false, nota: 'Critério de aceite. Só um celular de verdade prova.' },
      { texto: 'Nenhuma credencial trafega para o cliente', feito: true, nota: 'O front nunca chama a Evolution: só a edge function, que resolve o segredo no Vault.' },
      { texto: 'Não-admin não acessa a tela', feito: true, nota: 'Bloqueio na tela E na edge function — esconder botão não é autorização.' },
    ],
  },
  {
    numero: 5,
    titulo: 'Envio genérico + WhatsApp não-oficial',
    responsavel: 'Victor',
    status: 'concluida',
    resumo: 'Fecha o ciclo: responder de dentro do sistema. Backend, composer e worker no ar, testados de ponta a ponta.',
    alerta:
      'O que sobra não é da fase: quando o chip definitivo chegar, ele precisa ser um número NOVO. Um telefone com histórico dispara evento para todas as conversas antigas — na primeira tentativa vieram 71 eventos de 5 números diferentes, e a janela teve que ser encerrada pela metade.',
    checklist: [
      { texto: 'inbox-send: autentica, valida, enfileira, devolve para render otimista', feito: true },
      { texto: 'inbox-worker-outbound com retry e backoff', feito: true, nota: '30s → 2min → 8min, máximo 3 tentativas.' },
      { texto: 'adapter.send e assertCanSend', feito: true },
      { texto: 'Retry manual (botão "tentar de novo")', feito: true },
      { texto: 'Broadcast ao enfileirar', feito: true, nota: 'Sem ele, só quem apertou enter veria a mensagem.' },
      { texto: 'Testes (76 no total)', feito: true },
      { texto: 'Agendar o worker de saída no cron', feito: true, nota: 'Já estava agendado quando conferi, em 07/09: inbox-worker-outbound-minutely, respondendo {ok:true,enviados:0}.' },
      { texto: 'Composer dirigido por capabilities', feito: true, nota: 'Feito junto da tela da Fase 3, em 07/09. Envio, erro legível e botão de tentar de novo.' },
      { texto: 'Janela ao vivo: mensagem da UI chega ao celular', feito: true, nota: 'Testado em 07/09.' },
      { texto: 'A fila de saída deixou de ser inerte', feito: true, nota: 'Com o worker no cron, qualquer item enfileirado SAI de verdade. Teste de fila virou teste ao vivo — vale para as três filas.' },
    ],
  },
  {
    numero: 6,
    titulo: 'Chamados e protocolo',
    responsavel: 'Vinicius',
    status: 'concluida',
    dependeDe: [3],
    resumo: 'Protocolo VG-2026-000123 gerado no banco. Um ticket atravessa canais: N conversas por ticket.',
    alerta:
      'Divergência consciente do card: o protocolo NÃO usa sequence. Sequence não reinicia por ano sozinha e nextval não volta atrás em rollback — o primeiro chamado que falhasse numa validação deixaria buraco na numeração. Em vez disso, um contador por org e ano, incrementado com UPSERT ... RETURNING: serializa igual e é transacional.',
    checklist: [
      { texto: 'Tabelas inbox_tickets e inbox_ticket_conversations, com RLS', feito: true, nota: 'Entidade própria, não `tarefas`. Chamado é visível para toda a equipe, não só para o dono da conversa.' },
      { texto: 'Protocolo VG-2026-000123 gerado no banco, reiniciando por ano', feito: true, nota: 'O gerador vive em inbox_private: o PostgREST não expõe esse schema, então ninguém queima número pela API.' },
      { texto: 'Protocolo único e sequencial com dois atendentes ao mesmo tempo', feito: true, nota: 'Critério de aceite: 20 aberturas em paralelo, em conexões distintas — 20 protocolos, 0 duplicados, 0 buracos.' },
      { texto: 'Um ticket agrega conversas de canais diferentes', feito: true, nota: 'Critério de aceite: testado com 2 conversas no mesmo protocolo. O painel avisa quando há mais de uma.' },
      { texto: 'O protocolo é buscável e aparece na thread', feito: true, nota: 'Critério de aceite: selo no alto da conversa, marca na lista e busca junto com nome e telefone.' },
      { texto: 'Status, responsável e SLA de primeira resposta e resolução', feito: true, nota: 'O prazo é carimbado na abertura, não calculado na leitura: mudar a política depois não reescreve compromisso de chamado antigo.' },
      { texto: 'Primeira resposta carimbada sozinha', feito: true, nota: 'Trigger na mensagem que sai. A tela não é fonte confiável — o eco de uma resposta digitada no celular nunca passa por ela.' },
      { texto: 'Escalar para a TI criando solicitação vinculada na Central', feito: true, nota: 'Uma transação só, e o banco recusa o segundo clique: sem isso, dois cliques viram duas solicitações idênticas na fila.' },
      { texto: 'Testes dos formatadores de prazo', feito: true, nota: '13 testes em node:test, incluindo a virada do prazo.' },
    ],
  },
  {
    numero: 7,
    titulo: 'E-mail via Resend',
    responsavel: 'Victor',
    status: 'concluida',
    resumo: 'Segundo canal, no ar. Único com assunto, cópia e corpo HTML. O endereço público é contato@vigorenergy.com.br, reencaminhado para o subdomínio atende. — com cópia mantida na caixa original, então o pior caso é o módulo não receber, nunca o cliente ficar sem resposta.',
    alerta:
      'NUNCA deixar nada sobrescrever o MX do domínio RAIZ — e o risco não é só o Resend. Em 07/09 o painel da Hostinger ofereceu um botão "Sobrescrever e Aplicar" que trocaria MX, SPF e DMARC da raiz para o Google, chamando os registros da Vigor de "3 conflitos". O clique foi cancelado a tempo. Teria derrubado o e-mail da empresa inteira, com horas de propagação para voltar. O e-mail da Vigor é Microsoft 365, não Google Workspace.',
    checklist: [
      { texto: 'Adapter email: parse, threading, send', feito: true, nota: '24 testes. A ordem do agrupamento (cadeia antes do assunto) tem teste próprio.' },
      { texto: 'Assinatura Svix sobre o corpo cru', feito: true, nota: '14 testes, validados contra o node:crypto e não contra o próprio código.' },
      { texto: 'body_visible: corpo sem o trecho citado', feito: true, nota: 'Gmail, Outlook e Apple Mail. Não corta quando a citação está no meio do texto próprio.' },
      { texto: 'Edge function do webhook de e-mail', feito: true, nota: 'Deployada. Sem assinatura 401, forjada 401, evento cru gravado para auditoria.' },
      { texto: 'Workers generalizados para vários canais', feito: true, nota: 'Fixavam WhatsApp e kind=dm. Com e-mail, toda mensagem cairia na mesma conversa sem erro nenhum.' },
      { texto: 'Subdomínio, DNS e reencaminhamento', feito: true, nota: 'atende.vigorenergy.com.br verificado no Resend (região sa-east-1) e regra de fluxo criada no Microsoft 365. SPF e DKIM da raiz já existiam: era por isso que a cobrança já saía por e-mail.' },
      { texto: 'Validação de tamanho de anexo', feito: true, nota: 'Checagem nossa antes de enviar, com os dois números na mensagem. O tamanho vem do Storage, não do payload.' },
      { texto: 'Conexão de e-mail registrada', feito: true, nota: 'external_id é o endereço PÚBLICO, não o do subdomínio: o Resend manda received_for, e cadastrar o do subdomínio faria toda mensagem cair em "caixa não registrada" — resposta 200 e silêncio.' },
      { texto: 'O webhook do Resend traz apenas metadados', feito: true, nota: 'Descoberto na documentação antes do primeiro e-mail real. O adapter assumia corpo e anexos no payload; teria gravado toda mensagem vazia e aberto conversa nova a cada resposta, sem erro nenhum. Agora o worker busca o conteúdo pela API antes de gravar.' },
      { texto: 'Anexo de e-mail baixa de verdade', feito: true, nota: 'O código anterior afirmava que não existia download. Existia — em duas chamadas.' },
      { texto: 'Validado em produção', feito: true, nota: 'Assinatura real do Resend aceita, forjada e ausente recusadas com 401.' },
      { texto: 'Cópia mantida por padrão ao responder', feito: true, nota: 'Decisão do Victor. O agente pode remover antes de enviar; o nosso endereço nunca entra, senão vira laço.' },
      { texto: 'Renderização segura (iframe sandbox, imagem remota bloqueada)', feito: true, nota: 'O corpo vai para um iframe sem allow-scripts: o navegador não executa nada e o conteúdo não enxerga a sessão. Imagem remota bloqueada por padrão — a de 1x1 invisível existe para avisar o remetente de que a mensagem foi aberta. 19 testes.' },
      { texto: 'DOMPurify deixou de ser necessário', feito: true, nota: 'Estava na lista esperando aprovação. A proteção vem do isolamento do navegador, não de limpar HTML — nenhuma dependência nova entrou.' },
    ],
  },
  {
    numero: 8,
    titulo: 'Barramento de eventos e webhooks de saída',
    responsavel: 'Victor',
    status: 'concluida',
    dependeDe: [5],
    resumo: 'Sistemas externos (n8n) passam a saber o que acontece no Inbox: mensagem recebida, mensagem enviada, conversa atribuída, chamado aberto ou resolvido, e canal caiu. É também a fundação do motor de fluxo — a Fase 9 escuta este barramento em vez de observar o banco.',
    alerta:
      'É a primeira vez que o NOSSO servidor faz requisição para um endereço digitado por alguém. Por isso o destino é validado contra rede interna a cada entrega, e não só no cadastro: um endereço de metadados de nuvem transformaria o módulo em ponte para dentro da infraestrutura. O redirecionamento também é recusado — um endpoint público pode responder "vá para tal lugar" e furar a validação feita antes.',
    checklist: [
      { texto: 'Catálogo fechado de 6 eventos', feito: true, nota: 'Fechado de propósito: quem assinasse "mensagem.recebido" no masculino ficaria calado para sempre, sem erro. Agora o cadastro recusa na hora.' },
      { texto: 'Cadastro de destinos, com segredo próprio por endpoint', feito: true, nota: 'Segredo compartilhado deixaria um parceiro forjar evento para o outro.' },
      { texto: 'Assinatura HMAC no formato Svix', feito: true, nota: 'Quem recebe pode usar qualquer biblioteca Svix pronta em vez de reimplementar um HMAC caseiro — que é o que costuma sair errado.' },
      { texto: 'Emissão por gatilho no banco, não pelos workers', feito: true, nota: 'No worker, a próxima fase que gravasse mensagem por outro caminho deixaria de emitir sem erro nenhum, e ninguém descobriria até um fluxo parar de disparar.' },
      { texto: 'Entrega por fila com retry e backoff', feito: true, nota: '5 tentativas, de 15s a 1h. 4xx não é repetido: a resposta seria a mesma nas três vezes.' },
      { texto: 'Log de entrega: destino, resposta e motivo', feito: true, nota: 'Sem corpo de mensagem, telefone ou e-mail — só identificadores. Quem precisa do texto consulta a API com a própria credencial.' },
      { texto: 'Tela de log em Omnichannel → Integrações', feito: true, nota: 'A falha aparece primeiro e com o motivo à vista: quem abre a tela veio porque algo não disparou, e vai estar com pressa.' },
      { texto: 'Fluxo receptor criado e ativo no n8n', feito: true, nota: 'A instância bloqueia o módulo crypto nos nós de código; a verificação usa o nó Crypto nativo. Por isso o segredo tem hífens — assim os dois lados o tratam como texto puro. Trocar por um segredo comum faz toda entrega voltar 401.' },
      { texto: 'Validado em produção, ponta a ponta', feito: true, nota: 'Evento sai do banco, cron entrega, n8n aceita a assinatura. Sem assinatura ou forjada: 401. Destino quebrado tenta 5 vezes, desiste com o motivo registrado, e não atrasa o destino bom. Reconferido em 08/09 derrubando a conexão de novo: o gatilho emitiu sozinho e a entrega voltou 200.' },
      { texto: 'Testes (25)', feito: true, nota: 'A assinatura é gerada aqui e conferida pelo verificador da Fase 7 — dois códigos escritos separados. Conferir com a própria função de assinar faria um erro nos dois lados se cancelar.' },
    ],
  },
  {
    numero: 9,
    titulo: 'Motor de fluxo: modelo e executor',
    responsavel: 'Victor',
    status: 'concluida',
    dependeDe: [8],
    resumo: 'Máquina de estados por conversa, com versionamento: publicar fluxo novo não altera execução em andamento. Sem interface — o editor é a Fase 10 e os gatilhos são a Fase 11.',
    alerta:
      'A partir daqui quem escreve para o cliente é código, não pessoa. Por isso os quatro travamentos são requisito e não refinamento — e a detecção de laço fecha o caminho PADRÃO, não uma exceção: o passo envia, o gatilho do banco avisa "mensagem enviada", o fluxo avança e envia de novo. A trava de "um fluxo por conversa" não resolve isso: o fluxo é o mesmo, ele só avança para sempre.',
    checklist: [
      { texto: 'Modelo de dados: fluxos, versões, passos, ligações, execuções e trilha', feito: true, nota: 'Passos e ligações pertencem à VERSÃO, não ao fluxo. É o que faz publicar versão nova não quebrar execução em andamento, sem nenhum código de migração de estado.' },
      { texto: 'TRAVA 1 — detecção de laço', feito: true, nota: 'O fluxo guarda o id da mensagem que acabou de mandar e ignora o próprio eco. Barrado na PRIMEIRA volta: se dependesse do limite, o cliente receberia cinco mensagens antes de alguém perceber.' },
      { texto: 'TRAVA 2 — limite de mensagens automáticas por conversa', feito: true, nota: 'Rede de segurança do que não foi previsto: um teto simples não depende de entender o erro para conter o estrago.' },
      { texto: 'TRAVA 3 — atendente respondeu, o fluxo cala', feito: true, nota: 'Limitação conhecida: resposta digitada no CELULAR chega sem autor, e o WhatsApp não-oficial não distingue isso de resposta do cliente. O WhatsApp oficial carrega essa informação.' },
      { texto: 'TRAVA 4 — chave geral de desligamento, por empresa e por fluxo', feito: true, nota: 'Lida do banco a cada passo, nunca em cache. Quando um fluxo disparar errado às 22h de um sábado, ninguém vai fazer deploy.' },
      { texto: 'A ordem das travas importa', feito: true, nota: 'Laço é checado ANTES do limite. Um fluxo em laço estoura o limite naturalmente — invertido, o motivo registrado seria "atingiu o limite" e o laço ficaria escondido atrás do próprio sintoma.' },
      { texto: 'Oito tipos de passo, incluindo abrir chamado', feito: true, nota: 'O de chamado só foi possível porque a Fase 6 entregou o protocolo. O documento da fase dizia que era impossível.' },
      { texto: 'Toda parada tem motivo escrito em português', feito: true, nota: 'Fluxo parado sem motivo é indistinguível de fluxo travado.' },
      { texto: 'Mensagem automática usa lista fechada de campos', feito: true, nota: 'Um {{contato.telefone}} digitado por engano no editor mandaria o telefone do cliente de volta para ele dentro do texto. Com a lista fechada, o pior caso é o campo ficar vazio.' },
      { texto: 'Condição com operador fora da lista é recusada, não interpretada', feito: true, nota: 'Interpretar seria ramificar para o lado errado em silêncio, por semanas. Nada de eval nem expressão livre: o editor da Fase 10 é operado por quem atende, não por quem programa.' },
      { texto: 'O fluxo envia pelo mesmo caminho do envio manual', feito: true, nota: 'Mesma checagem de política e mesma fila. Fluxo não fura janela de 24h nem cria um segundo caminho de envio.' },
      { texto: 'Parada de emergência: interrompe todos os fluxos em andamento', feito: true, nota: 'Separada da chave geral de propósito: desligar a automação e cancelar o que já está rodando são decisões diferentes.' },
      { texto: '56 testes automatizados', feito: true, nota: 'E o contrário de cada trava também tem teste — a resposta de verdade do cliente continua avançando o fluxo. Trava larga demais pararia o caso legítimo e pareceria "não funciona".' },
      { texto: 'VALIDADO EM PRODUÇÃO ponta a ponta', feito: true, nota: 'Fluxo real publicado, iniciado e concluído, com a trilha passo a passo — de propósito um que NÃO envia mensagem (condição → etiqueta → fim). Segundo clique recusado, condição ramificou pelos dois lados, etiqueta aplicada de verdade. Banco limpo depois.' },
      { texto: 'CORRIGIDO no teste: um passo por minuto', feito: true, nota: 'O executor reenfileirava para continuar, mas o lote da rodada já tinha sido lido — o cliente esperaria minutos entre responder e ser respondido. Nenhum teste unitário pegaria: a lógica estava certa, o ritmo de quem a chamava é que estava errado. Agora o fluxo inteiro roda em menos de um segundo.' },
      { texto: 'CORRIGIDO no teste: publicação recusava chamada de sistema', feito: true, nota: 'Respondia "sem permissão" para quem tinha todas as permissões, porque checava o usuário logado e não havia um.' },
      { texto: 'Versão sem nenhuma ligação entre passos não publica', feito: true, nota: 'Guarda acrescentada depois de o motor parecer quebrado num teste: o grafo estava pela metade e o fluxo concluía no primeiro passo, sem explicação.' },
      { texto: 'Gatilhos automáticos (palavra-chave, fora do horário)', feito: true, nota: 'Entregue na Fase 11. O de "sem resposta há X" precisou ser varredura e não gatilho de banco: nada acontece quando o tempo passa — é a ausência de evento que dispara.' },
      { texto: 'Editor visual para montar o fluxo', feito: true, nota: 'Entregue na Fase 10. O motor não mudou: o editor grava o mesmo grafo que o executor já lia.' },
    ],
  },
  {
    numero: 10,
    titulo: 'Motor de fluxo: editor visual',
    responsavel: 'Vinicius',
    status: 'concluida',
    dependeDe: [9],
    resumo: 'Canvas de nós e conexões, com validação antes de publicar. O motor da Fase 9 ganhou quem o opere — sem escrever SQL.',
    alerta:
      'A lib de grafo (reactflow) foi RECUSADA, e a solicitação pedia essa decisão explicitamente. O grafo aqui é pequeno e fechado (nove tipos de passo, saída única exceto na condição), então o que a lib resolve de difícil não acontece — o canvas é 300 linhas de SVG. O que se perde é zoom e minimapa: num fluxo de vinte passos o canvas rola em vez de encolher.',
    checklist: [
      { texto: 'Canvas com nós, ligações e arrasto, sem dependência nova', feito: true },
      { texto: 'Painel de propriedades desenhado pelo catálogo, não por switch', feito: true, nota: 'Com switch, acrescentar um tipo de passo exigiria lembrar do painel — e o esquecimento não daria erro: o passo apareceria com o painel em branco.' },
      { texto: 'Validação antes de publicar: nó órfão, ramo sem saída, ciclo', feito: true, nota: '26 testes em node --test. É a única parte da fase que não depende de navegador, e é a que decide se um fluxo vai ao ar.' },
      { texto: 'Erro aponta o passo culpado pelo nome, e o clique leva até ele', feito: true },
      { texto: 'Losango publica: os dois ramos terminando no mesmo passo não é ciclo', feito: true, nota: 'É o desenho mais comum de todos. Uma validação larga demais quebraria o caso legítimo, e quem monta não teria como saber que o errado é o validador.' },
      { texto: 'Versão publicada é somente leitura; editar cria versão nova', feito: true, nota: 'Sai de graça do modelo da Fase 9 — nós e arestas pertencem à versão, não ao fluxo.' },
      { texto: 'Rola dentro do próprio contêiner, sem empurrar a página', feito: true },
      { texto: 'Um fluxo de verdade montado por quem atende', feito: false, nota: 'A tela está no ar e nenhum fluxo existe ainda. O primeiro é decisão de operação, não de programação.' },
    ],
  },
  {
    numero: 11,
    titulo: 'Motor de fluxo: gatilhos, simulação e publicação',
    responsavel: 'Vinicius',
    status: 'concluida',
    dependeDe: [10],
    resumo: 'Os cinco gatilhos, a regra de precedência, o modo simulação (roda o fluxo sem mandar nada para ninguém) e o painel de execução.',
    alerta:
      'É a fase em que o motor passa a agir SOZINHO: até aqui alguém apertava um botão, a partir daqui a mensagem do cliente é o botão. As quatro travas da Fase 9 continuam valendo a cada passo; esta fase acrescenta três contenções na porta de entrada — um fluxo por mensagem, nada dispara por mensagem nossa, e nada dispara em conversa que já tem fluxo rodando.',
    checklist: [
      { texto: 'Cinco gatilhos: palavra-chave, primeira mensagem, fora do horário, evento e sem resposta há X', feito: true },
      { texto: 'Palavra-chave ignora acento e maiúscula', feito: true, nota: 'Comparação sensível a acento simplesmente não dispararia, sem erro nenhum para investigar. Mesma normalização que o avaliador de condição faz do lado do TypeScript.' },
      { texto: 'Lista de palavras vazia NÃO casa com nada', feito: true, nota: 'O contrário transformaria um gatilho salvo pela metade em resposta automática para a empresa inteira.' },
      { texto: '"Sem resposta há X" é varredura, não gatilho de banco', feito: true, nota: 'Nada acontece quando o tempo passa: é a ausência de evento que dispara, então não há INSERT em que pendurar trigger. Roda no worker, uma vez por conversa por fluxo.' },
      { texto: 'Precedência explícita, e visível na tela na mesma ordem do motor', feito: true, nota: 'Menor prioridade primeiro; empate, o mais antigo. A view usa a MESMA cláusula ORDER BY da função que decide — quem monta o fluxo não precisa acreditar na documentação.' },
      { texto: 'Simulação usa o MESMO avancar() da produção', feito: true, nota: 'Simular no navegador seria um segundo motor "parecido" com o de verdade — e toda divergência apareceria como fluxo que funciona na simulação e trava no cliente, que é o problema que a simulação existe para evitar.' },
      { texto: 'Simulação não envia nada, e adianta o relógio das esperas', feito: true },
      { texto: 'Chave geral no topo da tela, com efeito no próximo passo', feito: true },
      { texto: 'Desativar fluxo PERGUNTA se interrompe os runs em andamento', feito: true, nota: 'O critério da fase pedia que interrompesse; a Fase 9 decidiu que desligar e cancelar são coisas diferentes. A pergunta resolve as duas: quem desativa escolhe, em vez de descobrir depois o que o sistema fez.' },
      { texto: 'Painel: quantos runs, onde pararam, onde falharam', feito: true },
      { texto: 'Provado em produção com rollback', feito: true, nota: 'Fluxo criado, publicado e disparado por mensagem real: 1 run com a palavra-chave, nenhum a mais na segunda mensagem, nenhum por mensagem nossa, nenhum por palavra que não casa. Nada ficou no banco.' },
      { texto: 'Um gatilho de verdade, ligado', feito: false, nota: 'Nenhum fluxo existe ainda, então nada dispara. Ligar o primeiro é decisão de operação.' },
    ],
  },
  {
    numero: 12,
    titulo: 'WhatsApp Cloud oficial',
    responsavel: 'Victor',
    status: 'concluida',
    dependeDe: [5],
    resumo: 'Canal oficial, com janela de 24h e template aprovado. O CÓDIGO ESTÁ PRONTO e o webhook no ar; o que falta é a liberação da Meta, que não é trabalho de programação.',
    alerta:
      'Usa um número DIFERENTE do que está na Evolution — um número não vive nos dois ao mesmo tempo. E a janela de 24h muda o atendimento, não só o código: passado um dia sem o cliente escrever, texto livre é recusado pela Meta e só template aprovado sai. A tela precisa dizer isso antes de a pessoa digitar.',
    checklist: [
      { texto: 'Tradutor do canal: recebe, envia, baixa mídia e confere a conexão', feito: true },
      { texto: 'Verificação da assinatura da Meta, escrita do zero', feito: true, nota: 'É a TERCEIRA forma de assinatura do módulo e não se parece com as outras duas. Copiar a do Resend ou o token da Evolution daria um verificador que recusa tudo — ou, pior, que aceita tudo.' },
      { texto: 'Resposta à verificação da URL', feito: true, nota: 'A Meta faz uma chamada de conferência ANTES de mandar qualquer mensagem. Sem tratar isso o webhook nunca é aceito, e o sintoma é "não chega nada" — sem erro em lugar nenhum.' },
      { texto: 'Mensagem e confirmação de entrega no mesmo envio', feito: true, nota: 'A Meta agrupa os dois. Tratar só um perde metade em silêncio.' },
      { texto: 'Janela de 24 horas', feito: true, nota: 'Fora dela, texto livre é recusado ANTES de gravar. Gravar e falhar depois deixaria a conversa mostrando ao atendente uma mensagem que o cliente nunca recebeu.' },
      { texto: 'Mensagem de tipo desconhecido não some', feito: true, nota: 'Vira aviso de sistema. Perder seria pior que mostrar sem formato — o atendente ao menos vê que algo chegou.' },
      { texto: 'Canal ligado ao módulo e webhook no ar', feito: true, nota: 'Registro do canal, segredo próprio e a função deployada. Testado: recusa verificação sem token e mensagem sem assinatura.' },
      { texto: 'Segredos da Meta liberados no cofre', feito: true, nota: 'Aplicado e conferido: os três nomes passaram a ser aceitos, nome fora da lista continua barrado, e os canais que já existiam não foram afetados — a função é a mesma para todos, e uma edição desatenta derrubaria WhatsApp e e-mail juntos.' },
      { texto: 'CORRIGIDO na revisão: nome do campo da credencial', feito: true, nota: 'O envio falharia dizendo "conexão sem token da Meta" com o token certo guardado no cofre. A mensagem mandaria procurar na credencial, quando o problema era o nome do campo.' },
      { texto: 'CORRIGIDO na revisão: o canal não estava registrado', feito: true, nota: 'Fora de propósito, para "não funcionar sem credencial". Mas no dia da estreia o erro seria "canal não registrado" — apontando para longe da credencial, que é o que de fato faltaria.' },
      { texto: '75 testes, em duas suítes independentes', feito: true, nota: '45 escritas junto com o código e 30 de segunda opinião, com os exemplos oficiais da Meta copiados literalmente e a assinatura conferida por outra biblioteca. Teste escrito por quem escreveu o código confirma as suposições do autor — foi assim que a Fase 7 quase gravou todo e-mail vazio.' },
      { texto: 'Verificação de negócio da Vigor na Meta', feito: false, nota: 'Business Manager → Central de Segurança. Ninguém olhou o selo ainda. Sabemos que existe conta com token de sistema, porque outra integração usa — mas isso não prova verificação.' },
      { texto: 'Aprovação do app para enviar mensagem', feito: false, nota: 'App Review. É o passo mais demorado.' },
      { texto: 'Segundo chip', feito: false, nota: 'Precisa ser diferente do que estiver no WhatsApp não-oficial.' },
      { texto: 'As credenciais da Meta', feito: false, nota: 'Id do número, chave do app e token permanente. O SQL para guardá-las está pronto, com onde achar cada uma e o que quebra se cada uma estiver errada.' },
      { texto: 'Cadastrar a URL no painel e assinar o campo de mensagens', feito: false, nota: 'Assinar o campo é separado de verificar a URL. Verificar sem assinar deixa tudo aparentemente certo e nada chegando.' },
      { texto: 'Sincronizar os templates aprovados', feito: false, nota: 'Depende da conta estar liberada.' },
    ],
  },
  {
    numero: 13,
    titulo: 'Instagram: DM e comentários',
    responsavel: 'Vinicius',
    status: 'em_andamento',
    dependeDe: [12],
    resumo: 'Comentário não é DM: thread própria, agrupada por post. O CÓDIGO ESTÁ PRONTO e o webhook no ar; o que falta é a liberação da Meta, que não é trabalho de programação.',
    alerta:
      'Responder no lugar errado aqui é responder EM PÚBLICO. O mesmo @fulano pode comentar num post e mandar direct no mesmo dia; se as duas coisas caíssem na mesma conversa, o atendente escreveria embaixo do post algo que era para ser privado. E o token do Instagram vence a cada ~60 dias: sem o job de renovação, o canal para sozinho dois meses depois de ligado, num dia em que ninguém mexeu em nada.',
    checklist: [
      { texto: 'Tradutor do canal: DM, comentário, anexo e conferência da conexão', feito: true },
      { texto: 'Comentário vira thread própria, agrupada pelo post', feito: true, nota: 'Não precisou de schema novo: o índice único de conversa já cobre kind + external_thread_id desde a Fase 0.' },
      { texto: 'Comentário e DM do mesmo usuário ficam separados', feito: true, nota: 'Com teste dedicado, comparando o par que o índice único usa.' },
      { texto: 'Três avisos na tela de que a resposta é pública', feito: true, nota: 'Selo na lista, selo no cabeçalho e faixa colada no campo de digitar. Um só não basta: a tela é idêntica à de um direct.' },
      { texto: 'O eco do nosso próprio comentário é ignorado', feito: true, nota: 'Responder um comentário faz o Instagram mandar webhook do NOSSO comentário. Sem filtrar, com o motor de fluxo ligado, seria resposta automática a nós mesmos, para sempre.' },
      { texto: 'Assinatura da Meta compartilhada com o WhatsApp Cloud', feito: true, nota: 'Extraída para _shared/adapters/meta.ts. Copiar verificação de assinatura é o pior tipo de duplicação: as duas cópias começam iguais, uma recebe correção e a outra não, e o sintoma é um canal aceitando webhook forjado. Os 174 testes de adapter continuam passando.' },
      { texto: 'Janela dupla: 24h comum, 7 dias com HUMAN_AGENT', feito: true, nota: 'Tratar como uma só recusaria mensagem legítima no terceiro dia — ou tentaria enviar no oitavo, com a mensagem já gravada.' },
      { texto: 'Resposta a comentário não tem janela', feito: true, nota: 'Post de duas semanas atrás continua respondível. Tratar como DM recusaria resposta legítima.' },
      { texto: 'Timestamp em MILISSEGUNDOS, ao contrário do WhatsApp', feito: true, nota: 'Tratar igual joga metade das mensagens para 1970, onde ficam presas no topo da thread para sempre, sem erro.' },
      { texto: 'Job diário de renovação do token', feito: true, nota: 'Renova com 10 dias de folga — margem para o job falhar uma semana seguida sem o canal cair. Falha marca a conexão como erro E emite evento: conexão vermelha numa tela que ninguém abre não avisa ninguém.' },
      { texto: 'A escrita no cofre tem a mesma lista fechada da leitura', feito: true, nota: 'É a primeira vez que o módulo escreve no Vault. O pior caso de um bug é sobrescrever um segredo do próprio Inbox — nunca alcançar segredo de outro módulo, que é o que um escritor genérico permitiria. Conferido em produção.' },
      { texto: '30 testes do adapter', feito: true },
      { texto: 'Conta do Instagram ligada à página do app', feito: false },
      { texto: 'App Review das permissões de mensagem e comentário', feito: false, nota: 'Sem a de comentários, responder comentário devolve 403. Sem human_agent, toda resposta depois de 24h é recusada.' },
      { texto: 'Token de acesso guardado no cofre', feito: false },
      { texto: 'URL cadastrada E os campos assinados', feito: false, nota: 'São duas coisas separadas. Verificada sem campo assinado, o painel fica todo verde e nada chega. Faltando só comments, o canal funciona pela metade e ninguém percebe.' },
      { texto: 'Cron diário da renovação agendado', feito: false },
    ],
  },
  {
    numero: 14,
    titulo: 'Organização do trabalho e busca',
    responsavel: 'Vinicius',
    status: 'concluida',
    dependeDe: [6],
    resumo: 'Atribuição, situação, etiquetas, notas internas, respostas rápidas, busca full-text multicanal, audit log e relatório de primeira resposta.',
    alerta:
      'Quase tudo já existia no banco desde a Fase 0 — o que faltava era o RASTRO. As ações passam por função SQL, e não por UPDATE direto, porque a policy de escrita do audit log é só de service_role: um log que o próprio autor da ação pode editar não é log. Pelo caminho direto, a conversa mudaria de dono sem deixar registro nenhum.',
    checklist: [
      { texto: 'Atribuir, mudar situação e etiquetar escrevem no audit log', feito: true, nota: 'Provado em produção agindo como o usuário logado de verdade, não como service_role — que passaria por cima da RLS e não provaria nada.' },
      { texto: 'Atribuir a quem não é do Omnichannel é recusado', feito: true, nota: 'Um id errado deixaria a conversa com alguém que não consegue abri-la: ela sumiria da fila de todo mundo, sem erro.' },
      { texto: 'Notas internas, com aviso colado no campo', feito: true, nota: 'Quem digita ali está a um clique do composer, que manda de verdade. O aviso existe para essa fração de segundo.' },
      { texto: 'Respostas rápidas por /atalho, filtradas por canal', feito: true, nota: 'Com a lista aberta, Enter ESCOLHE em vez de enviar — senão quem digitasse /saudacao mandaria a mensagem "/saudacao" para o cliente, literalmente.' },
      { texto: 'Busca full-text multicanal, usando o índice GIN', feito: true, nota: 'Virou função porque o índice é sobre uma EXPRESSÃO, e o PostgREST só sabe consultar coluna. Sem ela, a busca cairia num ilike que varre a tabela a cada tecla. Conferido: Bitmap Index Scan.' },
      { texto: 'A busca respeita a RLS', feito: true, nota: 'Sem security definer, de propósito: rodar como service_role faria da busca o jeito mais fácil de ver conversa dos outros.' },
      { texto: 'Etiquetas aparecem na lista de conversas', feito: true, nota: 'Ponto colorido, não chip com nome: a lista é virtualizada com altura fixa, e um chip empurraria a prévia da mensagem para fora.' },
      { texto: 'Relatório de primeira resposta por canal e por responsável', feito: true, nota: 'Mediana junto da média: numa amostra pequena, uma conversa respondida no dia seguinte joga a média para cima e faz o time inteiro parecer lento.' },
      { texto: 'Resposta automática de fluxo NÃO conta como primeira resposta', feito: true, nota: 'Contar mediria o robô, não o atendimento — e um fluxo de boas-vindas zeraria o indicador da empresa no dia em que fosse ligado.' },
      { texto: 'first_response_at preenchida retroativamente', feito: true, nota: 'A coluna existia desde a Fase 0 e ninguém preenchia. Sem o histórico, o relatório nasceria vazio e pareceria quebrado.' },
      { texto: 'Tela de atendimento: etiquetas, respostas, auditoria e relatório', feito: true },
      { texto: 'Assinatura de e-mail', feito: false, nota: 'Continua fora de escopo: é decisão de produto (por pessoa ou por caixa? com logo?) e faz mais sentido decidir quando a tela do e-mail existir.' },
    ],
  },
  {
    numero: 15,
    titulo: 'Endurecimento e LGPD',
    responsavel: 'Victor',
    status: 'concluida',
    dependeDe: [14],
    resumo:
      'Fechamento do módulo: ligar a conversa ao cadastro do cliente, exclusão por LGPD, limpeza automática dos eventos crus, alerta de fila travada — e as dívidas acumuladas nas fases anteriores.',
    alerta:
      'É a única fase que APAGA, e apagar errado é pior que não apagar. Uma exclusão por LGPD que deixa o arquivo no armazenamento não é exclusão: o registro some, a conversa some da tela, e a foto que o cliente mandou continua acessível — sem nada apontando para ela, logo sem ninguém para encontrá-la depois. O risco oposto também existe: a limpeza dos eventos derruba um mês inteiro de uma vez.',
    checklist: [
      { texto: 'Ligar a conversa ao cadastro do cliente — lógica pronta', feito: true, nota: 'Devolve uma LISTA de candidatos, nunca vínculo automático. Um mesmo e-mail aparece em 133 unidades consumidoras e um mesmo telefone em 50: casar sozinho acertaria o caso raro e erraria no de maior volume, colocando a conversa de uma pessoa no cadastro de outra. E o cadastro guarda "(64) 99275-8607" enquanto o Inbox guarda "+5564992758607" — sem normalizar os dois lados, não casa nada, e o sintoma é silêncio.' },
      { texto: '25 testes do casamento', feito: true },
      { texto: 'Confirmado: nenhum número pessoal segue conectado', feito: true, nota: 'A instância de teste com o WhatsApp do Victor foi desconectada e apagada em 07/09, antes de a chave da Evolution ser passada ao Vinicius. Reconferido em 08/09: zero instâncias.' },
      { texto: 'Aplicar o vínculo no banco e mostrar na tela', feito: true, nota: 'Com o motivo do palpite ("mesmo telefone da UC 12345") e opção de desfazer. A tela é da lane do Vinicius.' },
      { texto: 'Exclusão de contato a pedido (LGPD)', feito: true, nota: 'No ar. A ordem é arquivo primeiro, registro depois: invertido, uma falha no meio deixa arquivo órfão sem nada apontando para ele — e aí nem dá para saber que existe. Sem confirmação explícita, a função só MOSTRA o que seria apagado: o pedido chega por texto e alguém traduz para um id.' },
      { texto: 'Varrer o armazenamento atrás de arquivo órfão', feito: true, nota: 'Não basta apagar o que está referenciado — a Fase 2 teve um bug de duplicação e as Fases 5 e 7 têm caminhos de upload próprios. Primeira execução: zero órfãos.' },
      { texto: 'Registrar cada exclusão na trilha de auditoria', feito: true, nota: 'Quem pediu, quando e quantos itens. Nunca o conteúdo, nem nome ou telefone do titular — o objetivo do pedido é justamente eliminá-los. A trilha é gravada ANTES da exclusão: se ela falhar no meio, o registro é a única coisa que sobrevive.' },
      { texto: 'Limpeza automática dos eventos crus com mais de 30 dias', feito: true, nota: 'A tabela é dividida por mês, então a limpeza derruba a divisão inteira em vez de apagar linha a linha — é instantâneo e devolve o disco. NÃO foi agendada de propósito: agendar algo que apaga é uma decisão separada.' },
      { texto: 'Garantir que o mês seguinte é criado ANTES da limpeza', feito: true, nota: 'Está dentro da mesma função, nessa ordem. Invertido, um mês começa sem lugar para gravar e o módulo inteiro para de receber no dia 1º.' },
      { texto: 'Alerta de fila travada', feito: true, nota: 'NO AR, a cada 5 minutos. Era a única falha do módulo que era totalmente silenciosa. Duas medidas: a idade pega a fila parada, e o número de leituras pega o item repetindo erro — que a idade não pega, porque a fila anda e o item nunca sai. O teste revelou que o destino não assinava o evento novo: o alerta funcionaria sem chegar a ninguém, que é exatamente a falha que ele existe para evitar.' },
      { texto: 'Girar as duas chaves do WhatsApp não-oficial', feito: true, nota: 'Era a rotação mais séria: quem tivesse a chave da API controlava o WhatsApp da empresa. Conferido depois de girar — a chave guardada no cofre continua sendo aceita pelo servidor. Girar sem atualizar o cofre derrubaria envio e recebimento sem erro visível.' },
      { texto: 'Girar a chave do e-mail e o segredo do n8n', feito: true, nota: 'A do e-mail tinha passado por um arquivo do projeto, removida no mesmo dia e nunca enviada ao repositório. A do n8n eu mesmo gerei e ela apareceu em texto na conversa. Esta última é a mais fácil de girar pela metade — o segredo vive nos dois lados — então conferi emitindo um evento: entregue com 200.' },
      { texto: 'Remover o acesso total ao servidor do arquivo de configuração', feito: true, nota: 'Foi dado só para a Fase 1, quando a Evolution precisava subir. Desde então tudo passou pela API.' },
      { texto: 'Apagar os dados de exemplo', feito: false, nota: '546 mensagens, 5 contatos com nome e telefone reais vindos do WyzeChat. Servem para o Vinicius desenvolver a tela; saem quando ela não precisar mais.' },
      { texto: 'Comparação de assinatura em tempo constante no n8n', feito: true, nota: 'ESTAVA REGISTRADO ERRADO como "exige mudar o servidor". Só o CÁLCULO da assinatura dependia do recurso bloqueado, e quem calcula é um componente nativo do n8n — comparar com segurança é código simples. A comparação ingênua para no primeiro caractere diferente, e esse tempo permite descobrir o segredo aos poucos. Conferido nos dois sentidos: forjada continua recusada, evento real continua chegando.' },
      { texto: 'Conta contato@ deixou de ser Administrador Global — RESOLVIDO', feito: true, nota: 'Ela vai ser a caixa de atendimento, e quem responde cliente não pode estar numa conta que controla MX, regras de encaminhamento e senhas de todo mundo. Uma senha comprometida ali não vazaria só o atendimento.' },
      { texto: 'Controle de acesso das telas — CORRIGIDO', feito: true, nota: 'Deixava passar quem ainda não tinha o perfil carregado, em TODAS as rotas protegidas do sistema, não só as do Inbox: a condição inteira dava falso e a tela era renderizada. E o perfil fica ausente com mais frequência do que parece, porque a interface é destravada por timeout de 8s mesmo sem ele. Agora falha fechado. Custo conhecido: se o perfil falhar de verdade, a pessoa fica no painel até recarregar — que é justamente o que antes não acontecia.' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Item de maior prazo do módulo. Não é fase: corre em paralelo desde a Fase 1.
// ─────────────────────────────────────────────────────────────────────────────
const PARALELO = {
  titulo: 'Papelada da Meta',
  texto:
    'Verificação de negócio, criação do app, permissões e App Review. Leva semanas e bloqueia as Fases 12 e 13. Não bloqueia mais nada — mas começar tarde para o cronograma inteiro no fim.',
  nota:
    'A Vigor já usa a CAPI da Meta (meta-capi-purchase), o que prova que existe Business Manager com dataset e token. Não prova verificação de negócio concluída: CAPI funciona sem ela. Confirmar o selo em Central de segurança antes de decidir se o processo precisa ser aberto do zero.',
};

interface ConexaoAoVivo {
  id: string;
  display_name: string;
  channel_type: string;
  status: string;
  last_error: string | null;
  last_seen_at: string | null;
}

const ROTULO_STATUS: Record<StatusFase, string> = {
  concluida: 'Concluída',
  em_andamento: 'Em andamento',
  bloqueada: 'Aguardando',
  nao_iniciada: 'Não iniciada',
};

const CLASSE_STATUS: Record<StatusFase, string> = {
  concluida: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  em_andamento: 'bg-secondary/15 text-secondary dark:bg-secondary/20',
  bloqueada: 'bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400',
  nao_iniciada: 'bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400',
};

const CLASSE_CONEXAO: Record<string, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  qr_pending: 'bg-secondary/15 text-secondary dark:bg-secondary/20',
  pending: 'bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  disconnected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const IconeFase = ({ status }: { status: StatusFase }) => {
  if (status === 'concluida') return <CheckCircle2 size={20} className="text-green-600 dark:text-green-400 shrink-0" />;
  if (status === 'em_andamento') return <PlayCircle size={20} className="text-secondary shrink-0" />;
  if (status === 'bloqueada') return <Lock size={20} className="text-gray-400 shrink-0" />;
  return <Circle size={20} className="text-gray-400 shrink-0" />;
};

const OmnichannelView: React.FC = () => {
  const { profile } = useAuth();
  const [filtro, setFiltro] = useState<'todos' | Responsavel>('todos');
  const [conexoes, setConexoes] = useState<ConexaoAoVivo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroConexoes, setErroConexoes] = useState<string | null>(null);
  const [expandida, setExpandida] = useState<number | null>(1);

  // O RoleGuard da rota só bloqueia quando `profile.role` existe. O AuthContext
  // destrava a UI por timeout de 8s mesmo se o fetch do profile falhar — nesse
  // caso `profile` é null e o guard deixa passar. Como esta tela é de admin,
  // a checagem é refeita aqui.
  const ehAdmin = profile?.role === 'adm';

  const carregarConexoes = async () => {
    setCarregando(true);
    setErroConexoes(null);
    const { data, error } = await supabase
      .from('inbox_connections_public')
      .select('id, display_name, channel_type, status, last_error, last_seen_at')
      .order('created_at', { ascending: true });

    if (error) {
      // Erro esperado enquanto ninguém foi adicionado a inbox_members: a RLS
      // devolve vazio, não erro. Erro aqui é outra coisa — mostre o motivo.
      setErroConexoes(error.message);
      setConexoes([]);
    } else {
      setConexoes(data || []);
    }
    setCarregando(false);
  };

  useEffect(() => {
    if (ehAdmin) carregarConexoes();
    else setCarregando(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehAdmin]);

  const fasesVisiveis = useMemo(
    () => (filtro === 'todos' ? FASES : FASES.filter((f) => f.responsavel === filtro)),
    [filtro],
  );

  const progresso = useMemo(() => {
    const concluidas = FASES.filter((f) => f.status === 'concluida').length;
    return { concluidas, total: FASES.length, pct: Math.round((concluidas / FASES.length) * 100) };
  }, []);

  /**
   * Contagem dos ITENS de checklist, não das fases.
   *
   * "Fases concluídas" esconde o tamanho do que sobra: uma fase com um item
   * aberto e outra com doze contam igual. E o inverso também engana — as fases
   * paradas nesta rodada estão paradas por decisão (Meta) ou dependem de
   * terceiros, então "falta 1 fase" soa perto do fim quando não está.
   *
   * Aqui cada item vale um. É o número que responde "quanto falta de verdade".
   */
  /**
   * Contagem dos ITENS de checklist, não das fases, agrupados por QUEM
   * DESTRAVA.
   *
   * "Fases concluídas" escondia o tamanho do que sobra — uma fase com um item
   * aberto contava igual a outra com doze. E a lista corrida de itens abertos
   * escondia outra coisa: que a maioria não é trabalho parado, é espera. Juntas
   * numa lista só, "aprovação do app na Meta" e "montar um fluxo de verdade"
   * pareciam a mesma coisa, e não são — uma leva semanas e não depende de
   * ninguém aqui; a outra leva uma tarde.
   *
   * A classificação é por PALAVRA-CHAVE no texto do item, e é assim de
   * propósito: uma etiqueta manual em cada item se perderia na primeira vez que
   * alguém acrescentasse um item novo sem lembrar dela.
   */
  const itens = useMemo(() => {
    const todos = FASES.flatMap((f) =>
      (f.checklist ?? []).map((i) => ({ ...i, fase: f.numero })),
    );
    const feitos = todos.filter((i) => i.feito);
    const abertos = todos.filter((i) => !i.feito);

    const classificar = (texto: string): keyof typeof GRUPOS => {
      const t = texto.toLowerCase();
      if (/chip|pareamento|qr |webhook_global|conexão|menos de 5s|banner/.test(t)) return 'chip';
      if (
        /meta|app review|aprovação do app|instagram|template|credenciais|url no painel|url cadastrada|campos assinados|token de acesso|renovação/.test(
          t,
        )
      ) {
        return 'meta';
      }
      if (/apagar|sintético|dados de exemplo/.test(t)) return 'limpeza';
      if (/assinatura de e-mail/.test(t)) return 'decisao';
      // Trabalho que ainda não foi escrito, e não espera de terceiro. Fica
      // separado de propósito: misturado com "espera a Meta", some.
      if (/vínculo|aplicar no banco/.test(t)) return 'fazer';
      return 'uso';
    };

    const porGrupo: Record<string, typeof abertos> = {};
    for (const item of abertos) {
      const g = classificar(item.texto);
      (porGrupo[g] ??= []).push(item);
    }

    return {
      feitos: feitos.length,
      abertos: abertos.length,
      total: todos.length,
      pct: todos.length ? Math.round((feitos.length / todos.length) * 100) : 0,
      porGrupo,
    };
  }, []);

  if (!ehAdmin) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center px-4">
        <ShieldAlert size={40} className="mx-auto text-gray-400 mb-4" />
        <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Acesso restrito</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          O acompanhamento do módulo Omnichannel é visível apenas para administradores.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-0 space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-primary">
            <MessageSquare size={22} />
            <h1 className="text-2xl font-bold font-poppins text-gray-900 dark:text-gray-100">Omnichannel</h1>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
            Acompanhamento do Módulo Inbox Omnichannel — WhatsApp, Instagram e e-mail numa caixa de entrada única.
            Esta tela mostra o andamento; a caixa de entrada em si chega na Fase 3.
          </p>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {progresso.concluidas}
            <span className="text-lg font-medium text-gray-400"> / {progresso.total}</span>
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">fases concluídas</p>
        </div>
      </div>

      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progresso.pct}%` }} />
      </div>

      {/* Itens de checklist: feitos, e o que falta agrupado por quem destrava */}
      <div className="rounded-xl border border-secondary/30 bg-secondary/5 dark:bg-secondary/10 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <PlayCircle size={18} className="text-secondary" />
              <span className="text-xs font-semibold uppercase tracking-wide text-secondary">
                Itens das etapas
              </span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {itens.abertos === 0
                ? 'Todos os itens concluídos'
                : `${itens.abertos} ${itens.abertos === 1 ? 'item falta' : 'itens faltam'}`}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {itens.feitos} de {itens.total} concluídos. Quase nada do que falta é código
              parado — é espera.
            </p>
          </div>

          <div className="shrink-0 text-right">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {itens.feitos}
              <span className="text-lg font-medium text-gray-400"> / {itens.total}</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">itens</p>
          </div>
        </div>

        <div className="mt-3 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div
            className="h-full rounded-full bg-secondary transition-all"
            style={{ width: `${itens.pct}%` }}
          />
        </div>

        <div className="mt-4 space-y-4">
          {(Object.keys(GRUPOS) as (keyof typeof GRUPOS)[])
            .filter((g) => itens.porGrupo[g]?.length)
            .map((g) => {
              const grupo = GRUPOS[g];
              const lista = itens.porGrupo[g]!;
              return (
                <div key={g}>
                  <div className="mb-1.5 flex items-baseline gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${grupo.classe}`}>
                      {grupo.titulo} · {lista.length}
                    </span>
                    <span className="min-w-0 text-[11px] text-gray-500 dark:text-gray-400">
                      {grupo.explicacao}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {lista.map((item) => (
                      <li
                        key={`${item.fase}-${item.texto}`}
                        className="flex items-start gap-2 text-sm"
                      >
                        <Circle size={13} className="mt-1 shrink-0 text-gray-400" />
                        <span className="min-w-0 text-gray-700 dark:text-gray-200">
                          {/* A fase fica no item porque a lista atravessa todas:
                              sem ela, dois itens parecidos de fases diferentes
                              viram um só na cabeça de quem lê. */}
                          <span className="mr-1.5 rounded bg-gray-200/70 px-1.5 py-0.5 font-mono text-[10px] text-gray-600 dark:bg-slate-700 dark:text-slate-300">
                            F{item.fase}
                          </span>
                          {item.texto}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
        </div>
      </div>

      {/* Conexões ao vivo */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">Conexões de canal</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Estado real no banco, não o previsto no plano.</p>
          </div>
          <button
            onClick={carregarConexoes}
            disabled={carregando}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary disabled:opacity-50 transition-colors"
          >
            {carregando ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Atualizar
          </button>
        </div>

        {carregando ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Carregando…</p>
        ) : erroConexoes ? (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-3">
            <AlertTriangle size={16} className="mt-0.5 text-red-600 dark:text-red-400 shrink-0" />
            <p className="text-xs text-red-800 dark:text-red-300">Não foi possível ler as conexões: {erroConexoes}</p>
          </div>
        ) : conexoes.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nenhuma conexão registrada ainda — esperado até a Fase 1 fechar com o chip definitivo. A instância de
            teste não é registrada aqui de propósito: ela está pareada com um número pessoal.
          </p>
        ) : (
          <div className="space-y-2">
            {conexoes.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-100 dark:border-gray-700 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{c.display_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{c.channel_type}</p>
                  {c.last_error && <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{c.last_error}</p>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {c.last_seen_at && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock size={12} />
                      {new Date(c.last_seen_at).toLocaleString('pt-BR')}
                    </span>
                  )}
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      CLASSE_CONEXAO[c.status] || CLASSE_CONEXAO.pending
                    }`}
                  >
                    {c.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filtro por responsável */}
      <div className="flex items-center gap-2">
        <Users size={16} className="text-gray-400" />
        {(['todos', 'Victor', 'Vinicius'] as const).map((opcao) => (
          <button
            key={opcao}
            onClick={() => setFiltro(opcao)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filtro === opcao
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {opcao === 'todos' ? 'Todas as fases' : opcao}
          </button>
        ))}
      </div>

      {/* Roadmap */}
      <div className="space-y-2">
        {fasesVisiveis.map((fase) => {
          const aberta = expandida === fase.numero;
          const feitos = fase.checklist?.filter((i) => i.feito).length ?? 0;
          const total = fase.checklist?.length ?? 0;

          return (
            <div
              key={fase.numero}
              className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
            >
              <button
                onClick={() => setExpandida(aberta ? null : fase.numero)}
                className="w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
              >
                <IconeFase status={fase.status} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-mono text-gray-400">Fase {fase.numero}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CLASSE_STATUS[fase.status]}`}>
                      {ROTULO_STATUS[fase.status]}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{fase.responsavel}</span>
                    {total > 0 && (
                      <span className="text-xs text-gray-400">
                        {feitos}/{total}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 font-medium text-gray-900 dark:text-gray-100">{fase.titulo}</p>
                  {!aberta && (
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{fase.resumo}</p>
                  )}
                </div>
              </button>

              {aberta && (
                <div className="px-4 pb-4 pl-12 space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-300">{fase.resumo}</p>

                  {fase.dependeDe && fase.dependeDe.length > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Depende da {fase.dependeDe.map((n) => `Fase ${n}`).join(' e ')}.
                    </p>
                  )}

                  {fase.checklist && (
                    <ul className="space-y-1.5">
                      {fase.checklist.map((item) => (
                        <li key={item.texto} className="flex items-start gap-2 text-sm">
                          {item.feito ? (
                            <CheckCircle2 size={15} className="mt-0.5 text-green-600 dark:text-green-400 shrink-0" />
                          ) : (
                            <Circle size={15} className="mt-0.5 text-gray-400 shrink-0" />
                          )}
                          <span
                            className={
                              item.feito
                                ? 'text-gray-500 dark:text-gray-400'
                                : 'text-gray-800 dark:text-gray-200'
                            }
                          >
                            {item.texto}
                            {item.nota && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400">{item.nota}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {fase.alerta && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
                      <AlertTriangle size={16} className="mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
                      <p className="text-xs text-amber-800 dark:text-amber-300">{fase.alerta}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Item paralelo */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-5">
        <div className="flex items-center gap-2 mb-1">
          <Clock size={16} className="text-gray-400" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            {PARALELO.titulo} <span className="text-xs font-normal text-gray-500">— em paralelo, não é fase</span>
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">{PARALELO.texto}</p>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{PARALELO.nota}</p>
      </div>
    </div>
  );
};

export default OmnichannelView;
